///
/// EarthBrowser
///
/// Created by Matt Giger on 9/24/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Ground overlay primitive: user images draped on the globe, each
/// clipped to a lat/lon rectangle with its own opacity. All active
/// overlays share one material with slot-indexed uniforms (groundTex0..3)
/// because the tile renderer binds material uniforms by property name.
/// The slot count is baked into the shader via GROUND_OVERLAY_COUNT, so
/// the owning activity resets tile renderers when overlays come and go;
/// bounds/opacity edits are plain uniform updates (no recompile).
///

import { TextureOverlay } from "@/earthbrowser/datasets/TextureOverlay"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { type Material } from "@stovokor/shader/Material"
import { type Texture } from "@stovokor/shader/Texture"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { vec4 } from "@stovokor/math/vector"

export const cMaxGroundOverlays = 4

export interface GroundOverlayEntry {
	/** Dataset window id this overlay belongs to */
	id: string
	texture: Texture
	/** Normalized equirect UV bounds: minU, minV(north), maxU, maxV(south) */
	boundsUV: vec4
	/** 0..1 */
	opacity: number
}

/** Convert degree bounds to normalized equirect UV (north edge = minV) */
export function boundsToUV(west: number, south: number, east: number, north: number): vec4 {
	return new vec4(
		(west + 180) / 360,
		(90 - north) / 180,
		(east + 180) / 360,
		(90 - south) / 180,
	)
}

export class GroundOverlays extends TextureOverlay {
	public entries: GroundOverlayEntry[] = []

	constructor(context: EarthBrowserContext) {
		super(context)
	}

	public override overlayMaterial(): Material {
		return new GroundOverlaysMaterial(this)
	}

	/** No time series: nothing to advance per frame */
	public override frameUpdate(): void {}

	/** Texture lifetime is owned by GroundOverlayActivity, not the series */
	public override purge(): void {}
}

class GroundOverlaysMaterial implements Material {
	public shaderDefines: Record<string, string>
	public available: boolean = true
	public transparency: boolean = false

	// One uniform trio per active slot, created dynamically so the
	// material's enumerable properties exactly match the compiled shader
	// (the renderer warns about unmatched uniform names)
	private textures: UniformDynamic<Texture>[] = []
	private bounds: UniformDynamic<vec4>[] = []
	private opacities: UniformDynamic<number>[] = []

	private overlays: GroundOverlays

	constructor(overlays: GroundOverlays) {
		this.overlays = overlays
		const count = Math.min(overlays.entries.length, cMaxGroundOverlays)
		this.shaderDefines = { "GROUND_OVERLAY_COUNT": String(count) }

		const self = this as unknown as Record<string, unknown>
		for (let i = 0; i < count; i++) {
			this.textures.push(new UniformDynamic<Texture>())
			this.bounds.push(new UniformDynamic<vec4>())
			this.opacities.push(new UniformDynamic<number>())
			self[`groundTex${i}`] = this.textures[i]
			self[`groundBounds${i}`] = this.bounds[i]
			self[`groundOpacity${i}`] = this.opacities[i]
		}
	}

	public renderUpdate(): void {
		for (let i = 0; i < this.textures.length; i++) {
			const entry = this.overlays.entries[i]
			if (!entry) continue
			this.textures[i].value = entry.texture
			this.bounds[i].value = entry.boundsUV
			this.opacities[i].value = entry.texture.available ? entry.opacity : 0
		}
	}
}
